// YNX — MEMBERS MODULE (multi-user with RBAC, Google-authenticated)
// ───────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { Users, Trash2, ShieldCheck, Mail, RefreshCw, Copy } from 'lucide-react';
import { listMembers, updateMember, removeMember } from '../../lib/auth';

const ROLE_LABELS = {
  admin:      { label: 'Administrateur', color: '#fbbf24', desc: 'Gère tout (paramètres, abonnement, membres).' },
  accountant: { label: 'Comptable',      color: '#5eead4', desc: 'Devis, factures, clients uniquement.' },
  sales:      { label: 'Commercial',     color: '#a78bfa', desc: 'Agenda, tâches, clients, annuaire.' },
  member:     { label: 'Collaborateur',  color: '#94a3b8', desc: 'Tout sauf paramètres et abonnement.' },
};

export const MembersModule = ({ workspace, currentUser }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [copyMsg, setCopyMsg] = useState('');

  const isCurrentAdmin = (() => {
    if (!currentUser) return false;
    const me = members.find((m) => m.user_id === currentUser.user_id);
    return me && me.role === 'admin';
  })();

  const refresh = useCallback(async () => {
    setLoading(true); setErr('');
    try { setMembers(await listMembers()); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    setBusy(member.id);
    try { await updateMember(member.id, role); await refresh(); }
    catch (e) { alert(e.message); }
    finally { setBusy(''); }
  };

  const remove = async (member) => {
    if (!window.confirm(`Retirer ${member.name || member.email} de cet espace ?`)) return;
    setBusy(member.id);
    try { await removeMember(member.id); await refresh(); }
    catch (e) { alert(e.message); }
    finally { setBusy(''); }
  };

  const copyInvite = async () => {
    const url = `${window.location.origin}/?join=1`;
    const text = `Vous êtes invité·e à rejoindre l'espace YNX "${workspace.name}".

1. Allez sur ${url}
2. Connectez-vous avec votre compte Google
3. Entrez ce code d'accès : ${workspace.access_code}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg('✓ Lien d\'invitation copié');
      setTimeout(() => setCopyMsg(''), 2500);
    } catch (_e) { setCopyMsg('⚠ Copie impossible'); }
  };

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2><Users size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} /> Membres de l'équipe</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className='biz-btn-ghost sm' onClick={refresh} disabled={loading}>
            <RefreshCw size={12} /> Rafraîchir
          </button>
          <button className='biz-btn-primary sm' onClick={copyInvite} data-testid='copy-invite-btn'>
            <Copy size={12} /> Copier le lien d'invitation
          </button>
        </div>
      </header>
      {copyMsg && <div className='biz-banner'>{copyMsg}</div>}
      {err && <div className='biz-banner danger'>⚠ {err}</div>}

      <section className='biz-settings-section'>
        <h3>Comment inviter un collaborateur ?</h3>
        <ol style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-dim)', paddingLeft: 20 }}>
          <li>Partagez votre code d'accès <code className='biz-code-pill' style={{ fontSize: 12 }}>{workspace.access_code}</code> avec votre équipe.</li>
          <li>Chaque collaborateur se rend sur <b>{window.location.origin}/?join=1</b>.</li>
          <li>Il se connecte avec son compte Google, puis colle le code.</li>
          <li>Il apparaîtra ci-dessous avec le rôle <b>Collaborateur</b> — vous pourrez ensuite changer son rôle.</li>
        </ol>
      </section>

      <section className='biz-settings-section'>
        <h3>Membres actuels ({members.length})</h3>
        {loading && <div className='biz-empty'>Chargement…</div>}
        {!loading && members.length === 0 && (
          <div className='biz-empty'>
            Aucun membre authentifié pour le moment.<br/>
            (Vous pouvez toujours utiliser le code d'accès en mode anonyme — recommandé pour les solo.)
          </div>
        )}
        {!loading && members.length > 0 && (
          <table className='biz-table'>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Ajouté</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isMe = currentUser && m.user_id === currentUser.user_id;
                const roleMeta = ROLE_LABELS[m.role] || ROLE_LABELS.member;
                return (
                  <tr key={m.id}>
                    <td>
                      <b>{m.name || '(sans nom)'}</b>
                      {isMe && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--cyan)' }}>(vous)</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                      <Mail size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {m.email}
                    </td>
                    <td>
                      {isCurrentAdmin && !isMe ? (
                        <select
                          className='biz-input'
                          name={`role-${m.id}`}
                          value={m.role}
                          onChange={(e) => changeRole(m, e.target.value)}
                          disabled={busy === m.id}
                          data-testid={`role-select-${m.id}`}
                          style={{ width: 160, fontSize: 12 }}
                        >
                          {Object.keys(ROLE_LABELS).map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r].label}</option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className='biz-status'
                          style={{ background: 'rgba(0,0,0,0.18)', color: roleMeta.color, fontWeight: 700 }}
                        >
                          <ShieldCheck size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          {roleMeta.label}
                        </span>
                      )}
                      <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>{roleMeta.desc}</div>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                      {(m.created_at || '').slice(0, 10)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {isCurrentAdmin && !isMe && (
                        <button
                          className='biz-icon-btn danger'
                          onClick={() => remove(m)}
                          disabled={busy === m.id}
                          title='Retirer du workspace'
                          data-testid={`remove-member-${m.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};
